package com.tovtalk.ui.screen.settings

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Devices
import androidx.compose.material.icons.filled.PhoneAndroid
import androidx.compose.material.icons.filled.Computer
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import com.tovtalk.ui.scaffold.ScreenScaffold
import com.tovtalk.ui.scaffold.TovTalkTopBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.SessionInfo
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.ui.components.CenteredContentWrapper
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider
import kotlinx.coroutines.launch

@Composable
fun SessionsScreen(
    onNavigateBack: () -> Unit,
    userRepository: UserRepository = koinInject()
) {
    val scope = rememberCoroutineScope()
    var sessions by remember { mutableStateOf<List<SessionInfo>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var sessionToDelete by remember { mutableStateOf<SessionInfo?>(null) }
    var isDeletingSession by remember { mutableStateOf(false) }

    fun loadSessions() {
        loading = true
        errorMessage = null
        scope.launch {
            when (val result = userRepository.getSessions()) {
                is DomainResult.Success -> {
                    sessions = result.data
                    loading = false
                }
                is DomainResult.Error -> {
                    errorMessage = result.message
                    loading = false
                }
                is DomainResult.Loading -> {
                    // Already handled by loading state
                }
            }
        }
    }

    LaunchedEffect(Unit) {
        loadSessions()
    }

    // Delete confirmation dialog
    sessionToDelete?.let { session ->
        AlertDialog(
            onDismissRequest = { if (!isDeletingSession) sessionToDelete = null },
            title = { 
                Text(
                    StringProvider.get("sessions_disconnect_title"),
                    style = TovTalkTheme.typography.titleMedium
                ) 
            },
            text = { 
                Text(
                    StringProvider.get("sessions_disconnect_confirm"),
                    style = TovTalkTheme.typography.bodyMedium
                ) 
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        isDeletingSession = true
                        scope.launch {
                            when (val result = userRepository.deleteSession(session.id)) {
                                is DomainResult.Success -> {
                                    sessionToDelete = null
                                    isDeletingSession = false
                                    loadSessions() // Refresh list
                                }
                                is DomainResult.Error -> {
                                    sessionToDelete = null
                                    isDeletingSession = false
                                    errorMessage = result.message
                                }
                                is DomainResult.Loading -> {
                                    // Already handled by isDeletingSession state
                                }
                            }
                        }
                    },
                    enabled = !isDeletingSession,
                    colors = ButtonDefaults.textButtonColors(contentColor = TovTalkTheme.colors.error)
                ) {
                    if (isDeletingSession) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(16.dp),
                            strokeWidth = 2.dp
                        )
                    } else {
                        Text(StringProvider.get("sessions_disconnect"))
                    }
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { sessionToDelete = null },
                    enabled = !isDeletingSession
                ) {
                    Text(StringProvider.get("common_cancel"))
                }
            },
            containerColor = TovTalkTheme.colors.surfaceElevated
        )
    }

    ScreenScaffold(
        topBar = {
            TovTalkTopBar(
                title = StringProvider.get("sessions_title"),
                onBack = onNavigateBack,
            )
        },
        constrainContentWidth = false,
    ) { paddingValues ->
        val sessionsContent = @Composable {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
        ) {
            when {
                loading -> {
                    CircularProgressIndicator(
                        modifier = Modifier.align(Alignment.Center)
                    )
                }
                errorMessage != null -> {
                    Column(
                        modifier = Modifier
                            .align(Alignment.Center)
                            .padding(TovTalkTheme.spacing.lg),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            text = errorMessage ?: "Unknown error",
                            style = TovTalkTheme.typography.bodyMedium,
                            color = TovTalkTheme.colors.error
                        )
                        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
                        TextButton(onClick = { loadSessions() }) {
                            Text(StringProvider.get("common_retry"))
                        }
                    }
                }
                sessions.isEmpty() -> {
                    Text(
                        text = StringProvider.get("sessions_empty"),
                        style = TovTalkTheme.typography.bodyMedium,
                        color = TovTalkTheme.colors.textSecondary,
                        modifier = Modifier
                            .align(Alignment.Center)
                            .padding(TovTalkTheme.spacing.lg)
                    )
                }
                else -> {
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = androidx.compose.foundation.layout.PaddingValues(TovTalkTheme.spacing.md)
                    ) {
                        item {
                            Text(
                                text = StringProvider.get("sessions_description"),
                                style = TovTalkTheme.typography.bodySmall,
                                color = TovTalkTheme.colors.textSecondary,
                                modifier = Modifier.padding(
                                    horizontal = TovTalkTheme.spacing.xs,
                                    vertical = TovTalkTheme.spacing.sm
                                )
                            )
                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
                        }
                        
                        items(sessions) { session ->
                            SessionCard(
                                session = session,
                                onDisconnect = { sessionToDelete = session }
                            )
                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
                        }
                    }
                }
            }
        }
        }
        CenteredContentWrapper { sessionsContent() }
    }
}

@Composable
private fun SessionCard(
    session: SessionInfo,
    onDisconnect: () -> Unit
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = if (session.isCurrent) 
                TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.faint) 
            else 
                TovTalkTheme.colors.surfaceElevated
        ),
        shape = TovTalkTheme.shapes.medium
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(TovTalkTheme.spacing.md),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Device icon
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .clip(TovTalkTheme.shapes.small)
                    .background(TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.soft)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = if (session.isHadran) Icons.Default.PhoneAndroid 
                                 else if (session.userAgent?.contains("Mobile", ignoreCase = true) == true) Icons.Default.PhoneAndroid
                                 else Icons.Default.Computer,
                    contentDescription = null,
                    tint = TovTalkTheme.colors.brand,
                    modifier = Modifier.size(24.dp)
                )
            }

            Spacer(modifier = Modifier.width(TovTalkTheme.spacing.md))

            // Device info
            Column(modifier = Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = when {
                            session.isHadran -> StringProvider.get("sessions_device_hadran")
                            session.userAgent?.contains("Android", ignoreCase = true) == true -> "Android"
                            session.userAgent?.contains("iOS", ignoreCase = true) == true -> "iOS"
                            session.userAgent?.contains("Web", ignoreCase = true) == true -> "Web"
                            else -> StringProvider.get("sessions_device_unknown")
                        },
                        style = TovTalkTheme.typography.bodyLarge,
                        fontWeight = FontWeight.Medium,
                        color = TovTalkTheme.colors.textPrimary
                    )
                    
                    if (session.isCurrent) {
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            text = StringProvider.get("sessions_current_device"),
                            style = TovTalkTheme.typography.labelSmall,
                            color = TovTalkTheme.colors.brand,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
                
                Spacer(modifier = Modifier.height(4.dp))
                
                // Last seen
                Text(
                    text = "${StringProvider.get("sessions_last_seen")}: ${formatLastSeen(session.lastSeenAt)}",
                    style = TovTalkTheme.typography.bodySmall,
                    color = TovTalkTheme.colors.textTertiary
                )
            }

            // Disconnect button - disabled for current session
            if (!session.isCurrent) {
                TextButton(
                    onClick = onDisconnect,
                    colors = ButtonDefaults.textButtonColors(contentColor = TovTalkTheme.colors.error)
                ) {
                    Text(
                        text = StringProvider.get("sessions_disconnect"),
                        style = TovTalkTheme.typography.labelMedium
                    )
                }
            }
        }
    }
}

/**
 * Format last seen timestamp to relative time
 * TODO: Implement proper relative time formatting
 */
private fun formatLastSeen(timestamp: String): String {
    // For now, just return the timestamp
    // In production, parse and format as "2 minutes ago", "1 hour ago", etc.
    return try {
        timestamp.substringBefore('T')
    } catch (e: Exception) {
        timestamp
    }
}
